import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Form, Input, message, Modal, Radio, Select } from 'antd'
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { FormButton, SearchWrapper, WhiteWrapper } from './mappingKey.styled'
import MainTable from '../../components/Basic/MainTable'
import useParams from '../../hook/useParams'
import { addMappingKeys, checkMappingKeys, delMappingKeys, fetchMappingKeys, modMappingKeys } from '../../apis/source'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { fetchGroupList } from '../../apis/group'

function MappingKey (props) {
  const { adminMode } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowGroup } = user || {}
  const params = useParams({ includes: 'creator', groupIds: adminMode ? undefined : `0,${nowGroup}` })
  const { handleFetchParams, fetchParams } = params
  const [form] = Form.useForm()
  const [show, setShow] = useState(false)
  const [editData, setEditData] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [groupList, setGroupList] = useState([])

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`segment.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({
    name, label: ln_t(`segment.${name}`), rules: [{ required, message: defPlaceholder(name, type) }]
  })

  const handleDelete = (id, data) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${ln_t('sidebar.mappingkey')}`,
      content: `${ln_t('message.confirm.delete')} ${data.name}?`,
      onOk: async () => {
        const check = await checkMappingKeys(id)
        if (check?.status === 200) {
          const used = check?.data?.result
          if (used) message.error(ln_t('message.error.mappingKeyUsed'))
          else {
            const result = await delMappingKeys(id)
            if (result?.status === 204) {
              handleFetchParams('add', {})
              message.success(ln_t('message.success.delete'))
            }
          }
        }
      }
    })
  }

  const handleEdit = data => {
    setEditData(data)
    form.setFieldsValue(data)
    setShow(true)
  }
  const apiFunc = async () => fetchMappingKeys(fetchParams)
  const formatDate = (date, detail) => dayjs(date).format(detail ? 'DD-MM-YYYY HH:mm:ss' : 'DD-MM-YYYY')
  const columns = [
    { title: ln_t('util.name'), dataIndex: 'name', sorter: true },
    { title: ln_t('mappingKey.type'), dataIndex: 'groupId', render: e => e === 0 ? ln_t('mappingKey.default') : ln_t('mappingKey.custom') },
    { title: ln_t('dataSource.status'), dataIndex: 'status', render: e => e ? ln_t('util.enable') : ln_t('util.disable') },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', render: e => formatDate(e), sorter: true },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', render: e => formatDate(e), sorter: true },
    ...adminMode
      ? []
      : [{
          title: ln_t('util.action'),
          dataIndex: 'id',
          render: (id, data) => (
          <div className="action-btn">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(data)} disabled={data.groupId === 0} />
            <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(id, data)} disabled={data.groupId === 0} />
          </div>)
        }]
  ]

  const handleClose = () => {
    setShow(false)
    form.resetFields()
    setEditData()
  }

  const conf = {
    add: { api: val => addMappingKeys(val), success: 201, msg: ln_t('message.success.create') },
    edit: { api: (val, data) => modMappingKeys(data?.id, { ...data, ...val }), success: 200, msg: ln_t('message.success.modify') }
  }
  const handleSave = async value => {
    setIsLoading(true)
    const { api, success, msg } = conf[editData ? 'edit' : 'add']
    const result = await api({ ...value, groupId: nowGroup }, editData)
    if (result?.status !== success) return setIsLoading(false)
    handleFetchParams('add', {})
    message.success(msg)
    setIsLoading(false)
    handleClose()
  }
  const confirmMod = async value => {
    Modal.confirm({
      title: ln_t('message.confirm.modify'),
      content: `${ln_t('message.confirm.ifYouModify')} ${value?.name}, ${ln_t('message.confirm.modWillNotAffectPrevData')}`,
      onOk: async () => await handleSave(value)
    })
  }

  const getGroups = async () => {
    const res = await fetchGroupList({ maxResults: 999, noIdFilter: true })
    if (res?.status === 200) setGroupList(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }
  useEffect(() => { if (adminMode) getGroups() }, [])

  return (
    <>
      <WhiteWrapper>
        <h3>{ln_t('sidebar.mappingkey')}</h3>
        <SearchWrapper>
          <Input.Search
            allowClear
            style={{ width: '400px' }}
            placeholder={ln_t('util.search')}
            onSearch={e => handleFetchParams('add', { name_like: `%${e}%` })}
            enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
          />
          {adminMode
            ? <div className="d-flex">
                <div style={{ fontSize: '14px', fontWeight: 500, margin: 'auto 8px' }}>{ln_t('util.group')} :</div>
                <Select
                  showSearch
                  style={{ minWidth: '120px' }}
                  defaultValue="all"
                  placeholder={`${ln_t('util.select')} ${ln_t('util.group')}`}
                  options={[{ value: 'all', label: 'ALL' }, ...groupList]}
                  optionFilterProp="children"
                  onChange={e => handleFetchParams('add', { groupId: e === 'all' ? undefined : e })}
                />
              </div>
            : <Button type="default" icon={<PlusOutlined />} onClick={() => setShow(true)}>
                {ln_t('util.add')} {ln_t('mappingKey.mappingId')}
              </Button>
          }
        </SearchWrapper>

        <MainTable
          columns={columns}
          rowKey="id"
          apiFunc={apiFunc}
          params={params}
          disabledRow={record => !record.previewCount || !record.status}
        />
      </WhiteWrapper>

      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
        <Modal title={`${editData ? ln_t('util.edit') : ln_t('util.add')} ${ln_t('sidebar.mappingkey')}`} open={show} onCancel={handleClose} footer={null}>
          <Form form={form} layout="vertical" requiredMark={false} onFinish={e => editData ? confirmMod(e) : handleSave(e)}>
            <Form.Item {...defConfig('name', 'input')} style={{ marginTop: '16px' }}>
              <Input placeholder={defPlaceholder('name', 'input')} />
            </Form.Item>
            <Form.Item {...defConfig('status', 'select')} initialValue={1}>
              <Radio.Group
                options={[{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
            <FormButton>
              <Button type="default" onClick={handleClose}>{ln_t('util.cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
            </FormButton>
          </Form>
        </Modal>
      </ConfigProvider>
    </>
  )
}

export default MappingKey
